import { Component, OnInit } from '@angular/core';
import { Patient } from '../../../../calvin-emr-shared/models/patient.model';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { PatientDashboardTabComponent } from 'src/app/patients/dashboard-page/dashboard-tab/dashboard-tab.component';
import { FileUploadDialogComponent } from 'src/app/shared/file-upload-dialog/file-upload-dialog.component';
import { HistoryAndPhysical } from 'src/app/calvin-emr-shared/models/hist-and-phys.model';
import { PatientDashboardStore } from 'src/app/patients/state/dashboard-page.store';
import { DialogService } from 'primeng/dynamicdialog';
import { Store } from '@ngrx/store';
import { AccordionModule } from 'primeng/accordion';

@Component({
    selector: 'app-history-and-physical-tab',
    templateUrl: './history-and-physical-tab.component.html',
    styleUrls: ['./history-and-physical-tab.component.scss'],
    providers: [DialogService],
    standalone: true,
    imports: [AccordionModule],
})
export class HistoryAndPhysicalTabComponent extends PatientDashboardTabComponent implements OnInit {
  panelOpenState = false;
  histAndPhysForm = new HistoryAndPhysical();
  // Used for importing history and physical form
  importedPatientHistory: string[] = [];
  importErrors: string[] = [];
  patient: Patient = new Patient();

  constructor(
    patientStore: PatientDashboardStore,
    store: Store,
    private patientService: PatientService,
    private dialogService: DialogService
  ) {
    super(patientStore, store);
    this.patient$.subscribe((patient: Patient) => {
      this.patient = patient;
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initializeForm();
  }

  parseID(id: string): string {
    return id.includes('-') ? id.split('-')[1] : id;
  }

  initializeForm(): void {
    //PatientInfo
    this.histAndPhysForm.informant = this.patient.getChiefInformant() || '';
    this.histAndPhysForm.chiefComplaint = this.patient.getChiefComplaint() || '';
    this.histAndPhysForm.histOfCurrProb = this.patient.getHistOfCurrProb() || '';
    this.histAndPhysForm.allergies = this.patient.getAllergies() || '';
    this.histAndPhysForm.familyHistory = this.patient.getFamilyHistory() || '';
    this.histAndPhysForm.socialHistory = this.patient.getSocialHistory() || '';
    //PastMedicalHistory
    this.histAndPhysForm.previousIllnesses = this.patient.getPreviousIllness() || '';
    this.histAndPhysForm.injuries = this.patient.getInjuries() || '';
    this.histAndPhysForm.dietHistory = this.patient.getDietHistory() || '';
    this.histAndPhysForm.currentMeds = this.patient.getCurrentMeds() || '';
    this.histAndPhysForm.contagiousDiseases = this.patient.getContagiousDiseases() || '';
    this.histAndPhysForm.surgicalHistory = this.patient.getSurgicalHistory() || '';
    this.histAndPhysForm.otherPastMedHist = this.patient.getOtherPastMedHist() || '';
    //ReviewOfSystems
    this.histAndPhysForm.heent = this.patient.getHEENT() || '';
    this.histAndPhysForm.lymphatic = this.patient.getLymphatic() || '';
    this.histAndPhysForm.integument = this.patient.getIntegument() || '';
    this.histAndPhysForm.respiratory = this.patient.getRespiratory() || '';
    this.histAndPhysForm.cardiovascular = this.patient.getCardiovascular() || '';
    this.histAndPhysForm.gastrointestinal = this.patient.getGastrointestinal() || '';
    this.histAndPhysForm.developmental = this.patient.getDevelopmental() || '';
    this.histAndPhysForm.neurologic = this.patient.getNeurologic() || '';
    this.histAndPhysForm.endocrine = this.patient.getEndocrine() || '';
    this.histAndPhysForm.genitourinary = this.patient.getGenitoUrinary() || '';
    this.histAndPhysForm.musculoskeletal = this.patient.getMusculoskeletal() || '';
    //PhysicalExamination
    this.histAndPhysForm.peGeneral = this.patient.getPEGeneral() || '';
    this.histAndPhysForm.peVitalSigns = this.patient.getPEVitalSigns() || '';
    this.histAndPhysForm.peSkin = this.patient.getPESkin() || '';
    this.histAndPhysForm.peHEENT = this.patient.getPEHEENT() || '';
    this.histAndPhysForm.peCardio = this.patient.getPECardio() || '';
    this.histAndPhysForm.peRespiratory = this.patient.getPERespiratory() || '';
    this.histAndPhysForm.peGastrointestinal = this.patient.getPEGastrointestinal() || '';
    this.histAndPhysForm.peGenitourinary = this.patient.getPEGenitourinary() || '';
    this.histAndPhysForm.peMusculoskeletal = this.patient.getPEMusculoskeletal() || '';
    this.histAndPhysForm.peNeurologic = this.patient.getPENeurologic() || '';
    this.histAndPhysForm.peDevelopmental = this.patient.getPEDevelopmental() || '';
    this.histAndPhysForm.peEndocrine = this.patient.getPEEndocrine() || '';
    this.histAndPhysForm.peLymphatic = this.patient.getPELymphatic() || '';
    //Impressions
    this.histAndPhysForm.impressions = this.patient.getImpressions() || '';
    this.histAndPhysForm.plan = this.patient.getPlan() || '';
    this.histAndPhysForm.provSignature = this.patient.getProvSignature() || '';
    this.histAndPhysForm.impressionsDay = this.patient.getImpressionsDay() || '';
  }

  savePatientInfo(): void {
    if (this.patient && this.histAndPhysForm) {
      const updatedData: any = {
        //PatientInfo
        chiefInformant: this.histAndPhysForm.chiefComplaint,
        chiefComplaint: this.histAndPhysForm.chiefComplaint,
        histOfCurrProb: this.histAndPhysForm.histOfCurrProb,
        allergies: this.histAndPhysForm.allergies,
        familyHistory: this.histAndPhysForm.familyHistory,
        socialHistory: this.histAndPhysForm.socialHistory,
        //PastMedicalHistory
        previousIllness: this.histAndPhysForm.previousIllnesses,
        injuries: this.histAndPhysForm.injuries,
        dietaryHistory: this.histAndPhysForm.dietHistory,
        currentMeds: this.histAndPhysForm.currentMeds,
        contagiousDiseases: this.histAndPhysForm.contagiousDiseases,
        surgicalHistory: this.histAndPhysForm.surgicalHistory,
        otherPastMedHist: this.histAndPhysForm.otherPastMedHist,
        //ReviewOfSystems
        heent: this.histAndPhysForm.heent,
        lymphatic: this.histAndPhysForm.lymphatic,
        integument: this.histAndPhysForm.integument,
        respiratory: this.histAndPhysForm.respiratory,
        cardiovascular: this.histAndPhysForm.cardiovascular,
        gastrointestinal: this.histAndPhysForm.gastrointestinal,
        developmental: this.histAndPhysForm.developmental,
        neurologic: this.histAndPhysForm.neurologic,
        endocrine: this.histAndPhysForm.endocrine,
        genitourinary: this.histAndPhysForm.genitourinary,
        musculoskeletal: this.histAndPhysForm.musculoskeletal,
        //PhysicalExamination
        peGeneral: this.histAndPhysForm.peGeneral,
        peVitralSigns: this.histAndPhysForm.peVitalSigns,
        peSkin: this.histAndPhysForm.peSkin,
        peHEENT: this.histAndPhysForm.peHEENT,
        peCardio: this.histAndPhysForm.peCardio,
        peMusculoskeletal: this.histAndPhysForm.peMusculoskeletal,
        peGastrointestinal: this.histAndPhysForm.peGastrointestinal,
        peDevelopmental: this.histAndPhysForm.peDevelopmental,
        peNeurologic: this.histAndPhysForm.peNeurologic,
        peEndocrine: this.histAndPhysForm.peEndocrine,
        peGenitourinary: this.histAndPhysForm.peGenitourinary,
        peRespiratory: this.histAndPhysForm.peRespiratory,
        peLymphatic: this.histAndPhysForm.peLymphatic,
        //Impressions
        impressions: this.histAndPhysForm.impressions,
        plan: this.histAndPhysForm.plan,
        provSignature: this.histAndPhysForm.provSignature,
        impressionsDay: this.histAndPhysForm.impressionsDay,
      };

      Object.keys(updatedData).forEach((key) => {
        if (updatedData[key] === undefined) {
          delete updatedData[key];
        }
      });

      this.patient.historyAndPhysical = updatedData;

      this.patientService.upsertPatient(this.patient, 'studentPatients').subscribe(
        () => {
          alert('Patient data has been saved successfully for the original ID!');
          console.log('Original ID: ', this.patient.id);
          console.log('Original MRN: ', this.patient.mrn);
          console.log('Patient Phys & Hist: ', updatedData);

          // Update the data for the parsed patient ID
          const parsedID = this.parseID(this.patient.id);
          this.patient.id = parsedID; // Update patient ID
          this.patientService.upsertPatient(this.patient, 'patients').subscribe(
            () => {
              alert('Patient data has been saved successfully for the parsed ID!');
              console.log('Parsed ID: ', parsedID);
            },
            (error: any) => {
              console.error('Failed to save patient data for the parsed ID:', error);
              alert('Failed to save patient data for the parsed ID. Please try again.');
            }
          );
        },
        (error: any) => {
          console.error('Failed to save patient data for the original ID:', error);
          alert('Failed to save patient data for the original ID. Please try again.');
        }
      );
    }
  }

  createPatientHistory(patientHistoryList: string) {
    console.log(patientHistoryList);
    console.log('Create Patient History Test 1 - Load Data...Passed!');
    // Ignore first row, since first row is column headers
    const newPatientHistory = patientHistoryList.split('\r').slice(1);
    // element 0 is the MRN, and element 1 is the date of when the form was created
    // start at element 0 and remove the MRN and Date from the remaining array
    const mrn = newPatientHistory
      .splice(0, 2)[0]
      .split(',')
      .filter((x) => x != '')
      .map((x) => x.trim())[1]; // the 0th element is key "mrn" and element 1 is the value to "mrn"
    if (mrn == this.patient.id) {
      this.isLoading = true;
      console.log('Create Patient History Test 2 - Assign MRN and remove date...Passed!');

      // now, newPatientHistory should only have keys starting from "allergies"
      // get values from "values" column in .csv
      const histAndPhysVal: string[] = newPatientHistory.map(
        (string) => string.split(',').map((x) => x.trim())[1] // the value is element in position 1
      );

      const histAndPhysForm = new HistoryAndPhysical();
      console.log('Create Patient History Test 3 - created histAndPhysForm...Passed!');

      // assign values from .csv to attributes in the histAndPhysForm Object
      Object.keys(histAndPhysForm).forEach((key: string, index: number) => {
        histAndPhysForm[key] = histAndPhysVal[index];
      });
      console.log(histAndPhysForm);
      console.log('Create Patient History Test 4 - Mapped key-value pairs...Passed!');
      console.log(mrn);
      console.log('Create Patient History Test 5 - Retrieved MRN...Passed!');
      // turns the histAndPhysForm object into something that Firebase can understand when pushing to firebase
      const obj = JSON.parse(JSON.stringify(Object.assign({}, histAndPhysForm)));
      this.patientService.setPatient(mrn, { historyAndPhysical: obj }, 'patients'); // upload to firebase
      console.log('Create Patient History Test 6 - Pushing to firebase...Passed!');
      alert('Your history and physical form has been uploaded!');
      // Try to add data; if data is not valid, add to errors array
      this.stopLoading();
    } else {
      alert('MRN in the uploaded file does not match the current patient!');
    }

    // Display errors, if any
    if (this.importErrors.length > 0) {
      const errorString = this.importErrors.join('\n');
      console.error(
        'Could not import the following data:',
        errorString,
        '\nVerify that your file is correctly formatted and that no data is missing.'
      );
      // TODO: make this a dialog box
    }
  }

  openUploadPatientHistoryDialog() {
    this.importErrors = [];
    const dialogRef = this.dialogService.open(FileUploadDialogComponent, {
      header: 'Import History and Physical',
      width: '50%',
      data: this.importedPatientHistory,
    });

    // result is string, since fileReader returns string after parsing file
    dialogRef.onClose.subscribe((result: string) => {
      this.createPatientHistory(result);
    });
  }
}
